import clsx from "clsx";
import { format, parseISO } from "date-fns";
import Button from "devextreme-react/button";
import { CheckBox } from "devextreme-react/check-box";
import {
  Column,
  DataGrid,
  DataGridRef,
  FilterRow,
  HeaderFilter,
} from "devextreme-react/data-grid";
import { LoadPanel } from "devextreme-react/load-panel";
import { Popup } from "devextreme-react/popup";
import { SelectBox } from "devextreme-react/select-box";
import { Toolbar, Item as ToolbarItem } from "devextreme-react/toolbar";
import { confirm } from "devextreme/ui/dialog";
import notify from "../../lib/notify";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import {
  confirmPresencePositions,
  getPresencesToConfirm,
  rejectPresencePositions,
} from "../../pages/presence/api/Presence";
import { PerformanceToConfirmEntry, RoleEnum } from "../../types";
import { useScreenSize } from "../../utils/media-query";
import "./PresenceConfirmPopup.scss";

type PresenceConfirmPopupProps = {
  onClose: (changesHaveBeenMade: boolean) => void;
  from?: string;
  to?: string;
};

export default function PresenceConfirmPopup({
  onClose,
  from,
  to,
}: PresenceConfirmPopupProps) {
  const { t } = useTranslation();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;

  const authContext = useAuth();
  const companyContext = useCompanyContext();

  const dataGridRef = useRef<DataGridRef>(null);
  const [mobileSelectedIds, setMobileSelectedIds] = useState<number[]>([]);

  const [loadingMode, setLoadingMode] = useState<
    "fetchingData" | "processingRequest"
  >();
  const [errorMessage, setErrorMessage] = useState<string>();

  const [presencesToConfirm, setPresencesToConfirm] = useState<
    PerformanceToConfirmEntry[]
  >([]);
  const [employeePresenceToConfirm, setEmployeePresenceToConfirm] = useState<
    PerformanceToConfirmEntry["presencePositions"]
  >([]);

  const [employees, setEmployees] = useState<
    { value: string; display: string }[]
  >([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>();

  const [confirmRejectButtonsDisabled, setConfirmRejectButtonsDisabled] =
    useState(true);

  const [changesHaveBeenMade, setChangesHaveBeenMade] = useState(false);

  const isHeadAdministrator = authContext.user!.roles!.includes(
    RoleEnum.AdministratorGlowny,
  );
  const companyId = isHeadAdministrator ? companyContext.companyId : undefined;
  const headAdministratorDidNotSelectCompany =
    isHeadAdministrator && companyId === 0;

  const presencePositionsToConfirmCount = presencesToConfirm.reduce(
    (a, b) => a + b.presencePositions.length,
    0,
  );

  const fetchPresencesToConfirm = useCallback(async () => {
    try {
      setLoadingMode("fetchingData");
      setPresencesToConfirm(await getPresencesToConfirm(companyId, from, to));
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t("common.unknownErrorOccurred"),
      );
    } finally {
      setLoadingMode(undefined);
    }
  }, [companyId, from, to, t]);

  const handleAcceptButtonClick = useCallback(async () => {
    const selectedPresencePositionIds = isMobile
      ? mobileSelectedIds
      : (
          dataGridRef.current
            ?.instance()
            .option(
              "selectedRowKeys",
            ) as PerformanceToConfirmEntry["presencePositions"]
        ).map((row) => row.id);

    if (selectedPresencePositionIds.length === 0) {
      notify(t("presencePopupConfirm.noPresencesSelected"), "error", 5000);
      return;
    }

    try {
      setLoadingMode("processingRequest");
      await confirmPresencePositions(selectedPresencePositionIds);

      setLoadingMode("fetchingData");
      setPresencesToConfirm(await getPresencesToConfirm(companyId, from, to));
      setChangesHaveBeenMade(true);
      setMobileSelectedIds([]);

      notify(
        t("presencePopupConfirm.selectedPresencesHaveBeenConfirmed"),
        "success",
        5000,
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t("common.unknownErrorOccurred"),
      );
    } finally {
      setLoadingMode(undefined);
    }
  }, [companyId, from, to, isMobile, mobileSelectedIds, t]);

  const handleAcceptAllButtonClick = useCallback(async () => {
    setLoadingMode("processingRequest");

    for (const entry of presencesToConfirm) {
      await confirmPresencePositions(
        entry.presencePositions.map((pp) => pp.id),
      );
    }

    setLoadingMode("fetchingData");
    setPresencesToConfirm(await getPresencesToConfirm(companyId));
    setLoadingMode(undefined);
    setChangesHaveBeenMade(true);

    notify(
      t("presencePopupConfirm.selectedPresencesHaveBeenConfirmed"),
      "success",
      5000,
    );
  }, [companyId, from, to, presencesToConfirm, t]);

  const handleRejectButtonClick = useCallback(async () => {
    const selectedPresencePositionIds = isMobile
      ? mobileSelectedIds
      : (
          dataGridRef.current
            ?.instance()
            .option(
              "selectedRowKeys",
            ) as PerformanceToConfirmEntry["presencePositions"]
        ).map((row) => row.id);

    if (selectedPresencePositionIds.length === 0) {
      notify(t("presencePopupConfirm.noPresencesSelected"), "error", 5000);
      return;
    }

    try {
      setLoadingMode("processingRequest");
      const warning = await rejectPresencePositions(
        selectedPresencePositionIds,
      );

      setLoadingMode("fetchingData");
      setPresencesToConfirm(await getPresencesToConfirm(companyId, from, to));
      setChangesHaveBeenMade(true);
      setMobileSelectedIds([]);

      notify(
        t("presencePopupConfirm.selectedPresencesHaveBeenRejected"),
        "success",
        5000,
      );

      if (warning) {
        notify(warning, "warning", 8000);
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t("common.unknownErrorOccurred"),
      );
    } finally {
      setLoadingMode(undefined);
    }
  }, [companyId, from, to, isMobile, mobileSelectedIds, t]);

  useEffect(() => {
    if (headAdministratorDidNotSelectCompany) {
      setErrorMessage(
        t("presencePopupConfirm.headAdministratorDidNotSelectCompany"),
      );
      return;
    }

    fetchPresencesToConfirm();
  }, [headAdministratorDidNotSelectCompany, fetchPresencesToConfirm, t]);

  // generowanie listy pracowników po otrzymaniu obecności do zatwierdzenia
  useEffect(() => {
    const generateEmployees = () => {
      const employees = presencesToConfirm.map((entry) => ({
        value: entry.employee.id,
        display: `${entry.employee.fullName} (${entry.presencePositions.length})`,
      }));

      setEmployees(employees);

      if (employees.length > 0) {
        if (
          selectedEmployeeId === undefined ||
          !employees.some((e) => e.value === selectedEmployeeId)
        ) {
          setSelectedEmployeeId(employees[0].value);
        }
      }
    };

    generateEmployees();
  }, [presencesToConfirm, selectedEmployeeId]);

  // generowanie listy obecności do zatwierdzenia wyłączenie dla wybranego pracownika
  useEffect(() => {
    const generateEmployeePresencesToConfirm = () => {
      const presenceToConfirm = presencesToConfirm.find(
        (p) => p.employee.id === selectedEmployeeId,
      );
      if (presenceToConfirm) {
        setEmployeePresenceToConfirm(presenceToConfirm.presencePositions);
      }
    };

    generateEmployeePresencesToConfirm();
    setMobileSelectedIds([]);
  }, [selectedEmployeeId, presencesToConfirm]);

  const allMobileSelected = useMemo(
    () =>
      employeePresenceToConfirm.length > 0 &&
      mobileSelectedIds.length === employeePresenceToConfirm.length,
    [employeePresenceToConfirm, mobileSelectedIds],
  );

  if (loadingMode && presencePositionsToConfirmCount === 0) {
    return (
      <LoadPanel
        shading
        shadingColor="rgba(0, 0, 0, 0.3)"
        visible
        message={
          loadingMode === "processingRequest"
            ? t("common.processingRequest")
            : t("common.dataFetching")
        }
      />
    );
  }

  return (
    <Popup
      visible
      title={
        errorMessage
          ? t("common.errorOccured")
          : t("presencePopupConfirm.header", {
              value: presencePositionsToConfirmCount,
            })
      }
      onHiding={() => onClose(changesHaveBeenMade)}
      fullScreen={isMobile}
      maxWidth={isMobile ? undefined : 1200}
      height={isMobile ? "100%" : "auto"}
      maxHeight={isMobile ? undefined : "80vh"}
      showCloseButton
      dragEnabled={!isMobile}
      position={
        isMobile
          ? undefined
          : {
              my: {
                x: "center",
                y: "top",
              },
              at: {
                x: "center",
                y: "top",
              },
              offset: {
                x: 0,
                y: 80,
              },
            }
      }
    >
      <div className="presence-confirm-popup">
        {errorMessage ? (
          <div>{errorMessage}</div>
        ) : (
          <>
            {loadingMode ? (
              <LoadPanel
                shading
                shadingColor="rgba(0, 0, 0, 0.3)"
                visible
                message={
                  loadingMode === "processingRequest"
                    ? t("common.processingRequest")
                    : t("common.dataFetching")
                }
              />
            ) : null}
            {presencePositionsToConfirmCount === 0 ? (
              <div>{t("presencePopupConfirm.empty")}</div>
            ) : (
              <>
                {isMobile ? (
                  <div className="flex flex-col gap-3 mb-4">
                    <SelectBox
                      valueExpr="value"
                      displayExpr="display"
                      label={t("presencePopupConfirm.employee")}
                      labelMode="floating"
                      dataSource={employees}
                      value={selectedEmployeeId}
                      onValueChange={(employeeId) => {
                        setSelectedEmployeeId(employeeId);
                      }}
                      searchEnabled
                      width="100%"
                    />
                    <Button
                      type="default"
                      width="100%"
                      text={t("presencePopupConfirm.confirmAll")}
                      onClick={() => {
                        confirm(
                          t(
                            "presencePopupConfirm.youWantToApproveAllPresences",
                          ),
                          t("common.confirmation"),
                        ).then(async (decision) => {
                          if (decision) {
                            await handleAcceptAllButtonClick();
                          }
                        });
                      }}
                    />
                  </div>
                ) : (
                  <Toolbar className="mb-4">
                    <ToolbarItem location="before" locateInMenu="auto">
                      <SelectBox
                        valueExpr="value"
                        displayExpr="display"
                        label={t("presencePopupConfirm.employee")}
                        labelMode="floating"
                        dataSource={employees}
                        value={selectedEmployeeId}
                        onValueChange={(employeeId) => {
                          setSelectedEmployeeId(employeeId);
                          dataGridRef.current?.instance().clearFilter();
                          dataGridRef.current?.instance().clearSorting();
                          dataGridRef.current?.instance().clearSelection();
                        }}
                        searchEnabled
                        width={300}
                      />
                    </ToolbarItem>
                    <ToolbarItem location="after" locateInMenu="auto">
                      <Button
                        type="default"
                        text={t("presencePopupConfirm.confirmAll")}
                        onClick={() => {
                          confirm(
                            t(
                              "presencePopupConfirm.youWantToApproveAllPresences",
                            ),
                            t("common.confirmation"),
                          ).then(async (decision) => {
                            if (decision) {
                              await handleAcceptAllButtonClick();
                            }
                          });
                        }}
                      />
                    </ToolbarItem>
                  </Toolbar>
                )}
                {isMobile ? (
                  <div className="flex flex-col gap-2 mb-4">
                    <div className="flex items-center justify-between px-1">
                      <CheckBox
                        text={t("presencePopupConfirm.selectAll")}
                        value={allMobileSelected}
                        onValueChange={(value) => {
                          if (value === true) {
                            setMobileSelectedIds(
                              employeePresenceToConfirm.map((p) => p.id),
                            );
                          } else {
                            setMobileSelectedIds([]);
                          }
                        }}
                      />
                      <span className="text-sm opacity-75">
                        {`${mobileSelectedIds.length}/${employeePresenceToConfirm.length}`}
                      </span>
                    </div>
                    {employeePresenceToConfirm.map((row) => {
                      const isSelected = mobileSelectedIds.includes(row.id);
                      const toggle = () => {
                        setMobileSelectedIds((prev) =>
                          prev.includes(row.id)
                            ? prev.filter((id) => id !== row.id)
                            : [...prev, row.id],
                        );
                      };
                      const fromStr = row.from
                        ? format(parseISO(row.from), "dd.MM.yyyy HH:mm")
                        : "";
                      const toStr = row.to
                        ? format(parseISO(row.to), "dd.MM.yyyy HH:mm")
                        : "";
                      return (
                        <div
                          key={row.id}
                          className={clsx(
                            "flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer",
                            isSelected
                              ? "border-[#3c8dbc] bg-blue-50 dark:bg-blue-950"
                              : "border-gray-200 dark:border-gray-700",
                          )}
                          onClick={toggle}
                        >
                          <CheckBox
                            value={isSelected}
                            onValueChange={toggle}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold">
                              {fromStr}
                              {toStr ? (
                                <>
                                  <span className="mx-1 opacity-60">→</span>
                                  {toStr}
                                </>
                              ) : null}
                            </div>
                            {row.project?.name ? (
                              <div className="text-xs opacity-90 mt-1 break-words">
                                {`${t("presencePopupConfirm.project")}: ${row.project.name}`}
                              </div>
                            ) : null}
                            {row.note ? (
                              <div className="text-xs opacity-80 mt-1 break-words">
                                {`${t("presencePopupConfirm.note")}: ${row.note}`}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <DataGrid
                    className="mb-4"
                    ref={dataGridRef}
                    dataSource={employeePresenceToConfirm}
                    columnAutoWidth
                    showBorders
                    wordWrapEnabled
                    hoverStateEnabled
                    pager={{
                      showInfo: true,
                      showNavigationButtons: true,
                      showPageSizeSelector: true,
                      allowedPageSizes: [5, 10, 15, "all"],
                    }}
                    defaultPaging={{
                      pageSize: 5,
                    }}
                    selection={{
                      mode: "multiple",
                      selectAllMode: "page",
                    }}
                    onOptionChanged={(event) => {
                      if (event.fullName === "selectedRowKeys") {
                        setConfirmRejectButtonsDisabled(!event.value.length);
                      }
                    }}
                  >
                    <Column
                      dataField="from"
                      dataType="datetime"
                      format="dd.MM.yyyy HH:mm"
                      caption={t("presencePopupConfirm.startDate")}
                      width={200}
                    />
                    <Column
                      dataField="to"
                      dataType="datetime"
                      format="dd.MM.yyyy HH:mm"
                      caption={t("presencePopupConfirm.endDate")}
                      width={200}
                    />
                    <Column
                      dataField="project.name"
                      caption={t("presencePopupConfirm.project")}
                      width={250}
                    />
                    <Column
                      dataField="note"
                      caption={t("presencePopupConfirm.note")}
                    />
                    <FilterRow visible />
                    <HeaderFilter visible />
                  </DataGrid>
                )}
                <div
                  className={clsx(
                    "flex gap-3",
                    isMobile ? "flex-col" : "flex-row",
                  )}
                >
                  <Button
                    stylingMode="contained"
                    type="default"
                    width={isMobile ? "100%" : undefined}
                    text={t("presencePopupConfirm.confirm2")}
                    onClick={handleAcceptButtonClick}
                    disabled={
                      isMobile
                        ? mobileSelectedIds.length === 0
                        : confirmRejectButtonsDisabled
                    }
                  />
                  <Button
                    stylingMode="contained"
                    type="danger"
                    width={isMobile ? "100%" : undefined}
                    text={t("presencePopupConfirm.reject")}
                    onClick={handleRejectButtonClick}
                    disabled={
                      isMobile
                        ? mobileSelectedIds.length === 0
                        : confirmRejectButtonsDisabled
                    }
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Popup>
  );
}
